import { useState, useEffect } from 'react';

import { useParams } from 'react-router-dom';
import Papa from 'papaparse';
import fileSaver from 'file-saver';

import { Grid, Typography, useTheme, useMediaQuery } from '@mui/material';

import DataGrid, {
  DataGridModel,
  useDataGridState,
  LoadingState,
  ControlledDataGrid,
} from 'components/dataGrid/components/DataGrid';

import { DataGridColumnProps } from 'components/dataGrid/components/DataGridColumn';

import { useRightDrawerDispatcher } from 'providers/RightDrawerProvider/hooks/useRightDrawerDispatcher';

import OrderSummary from '../components/rightDrawer/OrganizerOrderSummary';

import { FilterType } from 'components/filters/filterTypes';
import { FilterState } from 'components/filters/Filters';

import { useCallService } from 'hooks';

import PageContainer from 'components/PageContainer';

import httpRoutes from 'utils/httpRoutes';
import { formatCurrency } from 'utils/helpers';
import { formatDate } from 'utils/formatDateTime';

const loadingState: LoadingState = {
  data: true,
  exporting: false,
};

const TransactionsGrid = () => {
  const { state } = useDataGridState();
  const { callService } = useCallService();
  const { showRightDrawer } = useRightDrawerDispatcher();

  const [loading, setLoading] = useState<LoadingState>(loadingState);
  // const [ticketTypes, setTicketTypes] = useState<any[]>([]);
  const [filters, setFilters] = useState<FilterState>(new Map());
  const [gridData, setGridData] = useState<DataGridModel>({
    rows: [],
    totalRows: 0,
  });

  const params = useParams();
  const { id } = params;
  const _eventId = Number(id);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    getEventTransactions();
  }, [state, filters]);

  // useEffect(() => {
  //   const getTicketTypes = async () => {
  //     if (_eventId) {
  //       const { response } = await callService({
  //         resource: httpRoutes.organizer.getAllTicketTypes(_eventId),
  //       });

  //       if (response) {
  //         const defaultOption = {
  //           label: 'Any Type',
  //           value: '',
  //         };

  //         const _ticketTypes = response.map(
  //           (item: { id: string; name: string }) => {
  //             return { value: item.id, label: item.name };
  //           }
  //         );

  //         _ticketTypes.unshift(defaultOption);

  //         setTicketTypes(_ticketTypes);
  //       }
  //     }
  //   };

  //   getTicketTypes();
  // }, []);

  const getOrderDetails = (orderId: number) => {
    showRightDrawer({
      content: <OrderSummary orderId={orderId} />,
    });
  };

  const getEventTransactions = async () => {
    try {
      setLoading((prevState) => ({ ...prevState, data: true }));

      const { response } = await callService({
        resource: httpRoutes.organizer.getTransactions({
          eventId: _eventId,
          params: { ...state },
          filters,
        }),
      });

      if (response) {
        setGridData({
          rows: response.items,
          totalRows: response.totalRows,
        });
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading((prevState) => ({ ...prevState, data: false }));
    }
  };

  const exportEventTransactions = async () => {
    try {
      setLoading((prevState) => ({ ...prevState, exporting: true }));

      const { response } = await callService({
        resource: httpRoutes.organizer.exportTransactions({
          eventId: _eventId,
          params: { ...state },
          filters,
        }),
      });

      if (response) {
        const csv = Papa.unparse(response);
        const csvData = new Blob([csv], { type: 'text/plain:charset=utf-8' });

        fileSaver.saveAs(csvData, 'Event Transactions.csv');
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading((prevState) => ({ ...prevState, exporting: false }));
    }
  };

  const filterModel: FilterType[] = [
    {
      id: 'customerName',
      type: 'text',
      label: 'Attendee/Sponsor',
    },
    {
      id: 'customerEmail',
      type: 'text',
      label: 'Email',
    },
  ];

  // set columns
  const columns: DataGridColumnProps[] = [
    {
      headerName: 'ID',
      field: 'id',
      sortable: false,
    },
    {
      headerName: 'Name',
      field: 'customerName',
      sortable: true,
      flex: 0.15,
    },
    {
      headerName: 'Email',
      field: 'customerEmail',
      sortable: true,
      hide: true,
      flex: 0.1,
    },
    {
      headerName: 'Purchased',
      field: 'datePurchased',
      sortable: true,
      type: 'dateTime',
      flex: 0.1,
    },
    {
      headerName: 'Status',
      field: 'status',
      sortable: true,
      hide: true,
    },
    {
      headerName: 'Card Type',
      field: 'creditCardType',
      sortable: true,
      hide: true,
    },
    {
      headerName: 'Subtotal',
      field: 'subtotal',
      sortable: true,
      type: 'usdCurrency',
      headerAlign: 'center',
      align: 'right',
      flex: 0.1,
    },
    {
      headerName: 'Fees',
      field: 'totalFees',
      sortable: true,
      headerAlign: 'center',
      type: 'usdCurrency',
      align: 'right',
      flex: 0.1,
    },
    {
      headerName: 'Total',
      field: 'total',
      sortable: true,
      type: 'usdCurrency',
      headerAlign: 'center',
      align: 'right',
      flex: 0.1,
    },
    {
      headerName: 'Transactions',
      field: 'mobile',
      flex: 1,
      ColumnComponent: (params: any) => {
        return (
          <Grid
            container
            height="100%"
            m={0.5}
            p={1}
            justifyContent="space-between"
            flexDirection="row"
            spacing={1}
          >
            <Grid item xs={12} sm={4}>
              <Typography variant="caption">Name</Typography>
              <Typography fontWeight="bold">
                {params.row.customerName}
              </Typography>
              <Typography>{params.row.customerEmail}</Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption">Date Purchased</Typography>
              <Typography>{formatDate(params.row.datePurchased)}</Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption">Totals</Typography>
              {Number(params.row.subtotal) > 0 && (
                <Typography>
                  Subtotal: {formatCurrency(params.row.subtotal)}
                </Typography>
              )}
              {Number(params.row.totalFees) > 0 && (
                <Typography>
                  Fees: {formatCurrency(params.row.totalFees)}
                </Typography>
              )}
              {Number(params.row.total) > 0 && (
                <Typography>
                  Total: {formatCurrency(params.row.total)}
                </Typography>
              )}
            </Grid>
          </Grid>
        );
      },
    },
  ];

  return (
    <PageContainer maxWidth="xl">
      <DataGrid
        tableTitle="Event Transactions"
        rows={gridData.rows}
        columns={columns}
        filters={filterModel}
        handleFilterChange={setFilters}
        totalRows={gridData.totalRows}
        loading={loading.data}
        handleExport={exportEventTransactions}
        onRowClick={(params) => {
          if (params.status !== 'cancelled') {
            getOrderDetails(Number(params.id));
          }
        }}
      />
    </PageContainer>
  );
};

const Transactions = () => (
  <ControlledDataGrid>
    <TransactionsGrid />
  </ControlledDataGrid>
);

export default Transactions;
